import { useRef } from 'react'

import { PayrollIssuesPagePaginatedDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useEmployeeFilters } from 'modules/employees/lib/filters'

const PER_PAGE = 25

export type PayrollIssueItem = import('generated/resources/graphql').PayrollIssuesTableIssueFragment

export interface UsePayrollIssuesDataCollectionSourceOptions {
  policyPeriodId: number
  getItemUrl: (issue: PayrollIssueItem) => string
}

export function usePayrollIssuesDataCollectionSource({
  policyPeriodId,
  getItemUrl,
}: UsePayrollIssuesDataCollectionSourceOptions) {
  const { filters: employeeFilters, hasJobCatalogTreeFeature } = useEmployeeFilters()
  const employeeFiltersRef = useRef(employeeFilters)
  employeeFiltersRef.current = employeeFilters

  return useGraphqlDataCollectionSource(
    {
      document: PayrollIssuesPagePaginatedDocument,
      perPage: PER_PAGE,
      paginationType: 'pages',
      mapVariables: ({ search, filters }) => {
        // Only use filter values that are arrays (selected IDs). options.filters can contain
        // filter definitions (objects) from source.filters; calling .map on those would throw.
        const teamIds = Array.isArray(filters?.team) ? filters.team : undefined
        const locationIds = Array.isArray(filters?.workplace) ? filters.workplace : undefined
        const legalEntityIds = Array.isArray(filters?.legalEntity) ? filters.legalEntity : undefined
        const jobRoleRaw = Array.isArray(filters?.jobRole) ? filters.jobRole : undefined
        const jobLevelIds =
          !hasJobCatalogTreeFeature && jobRoleRaw?.length
            ? jobRoleRaw.map((id: string | number) => Number(id))
            : undefined
        const jobCatalogTreeNodeIds =
          hasJobCatalogTreeFeature && jobRoleRaw?.length
            ? jobRoleRaw.map((id: string | number) => id.toString())
            : undefined
        const reportingTo = Array.isArray(filters?.reportingTo) ? filters.reportingTo : undefined
        const timeoffManagerIds = Array.isArray(filters?.timeoffManager)
          ? filters.timeoffManager
          : undefined
        const accessStatus = Array.isArray(filters?.accessStatus) ? filters.accessStatus : undefined

        return {
          policyPeriodId,
          search: search || undefined,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          legalEntityIds: legalEntityIds?.length ? legalEntityIds : undefined,
          referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
          referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
            ? jobCatalogTreeNodeIds
            : undefined,
          managerIds: reportingTo?.length ? reportingTo : undefined,
          timeoffManagerIds: timeoffManagerIds?.length ? timeoffManagerIds : undefined,
          accessStatus: accessStatus?.length ? accessStatus : undefined,
        }
      },
      mapPagination: (data) => ({
        total: data?.payroll?.payrollIssuesConnection?.totalCount ?? 0,
      }),
      mapData: (data) => data?.payroll?.payrollIssuesConnection?.nodes ?? [],
      itemUrl: getItemUrl,
      search: { enabled: true },
      filters: {
        workplace: employeeFiltersRef.current?.workplace,
        team: employeeFiltersRef.current?.team,
        legalEntity: employeeFiltersRef.current?.legalEntity,
        jobRole: employeeFiltersRef.current?.jobRole,
        accessStatus: employeeFiltersRef.current?.accessStatus,
        reportingTo: employeeFiltersRef.current?.reportingTo,
        timeoffManager: employeeFiltersRef.current?.timeoffManager,
      },
    },
    [policyPeriodId, getItemUrl, hasJobCatalogTreeFeature]
  )
}
